<script setup lang="ts">
import { computed, defineProps, onMounted, ref } from 'vue'
import type { Opportunity } from '@/api-swagger'
import OpportunityCreate from '@/components/features/opportunities/OpportunityCreate.vue'
import GridVue from '@/components/data-visualization/grids/GridVue.vue'
import OpportunityEdit from '@/components/features/opportunities/OpportunityEdit.vue'
import { dateComparator, formatDate } from '@/utils/dateUtils'
import ArrowForecast from '@/components/common/ui/ArrowForecast.vue'
import { currencyComparator, printCurrency } from '@/utils/numbers'
import { useOpportunityStore } from '@/stores/useOpportunityStore'
import { storeToRefs } from 'pinia'
import { useSelectionsStore } from '@/stores/useSelectionsStore'
import OpportunityGridActions from '@/components/features/opportunities/OpportunityGridActions.vue'

const opportunityStore = useOpportunityStore()

const { stages } = storeToRefs(opportunityStore)
const selectionsStore = useSelectionsStore()
const { salesFamilyOpportunitySelections, typeOfSaleOpportunitySelections } =
  storeToRefs(selectionsStore)

const props = defineProps({
  data: {
    type: Array<Opportunity>,
    required: true,
  },
  showAddButton: {
    type: Boolean,
    default: true,
  },
  filter: {
    type: Boolean,
    default: true,
  },
})

const showModal = ref(false)
const selectedOpportunity = ref<Opportunity | null>(null)

const stageMap = computed(
  () => new Map((stages.value || []).map((stage) => [stage.id, stage.name])),
)

const opportunitiesList = computed(() => {
  const list = props.data

  return (
    list?.map((opportunity: Opportunity) => {
      return {
        ...opportunity,
        _partnerName: opportunity.partnerId?.name || 'Inconnue',
        _typeOfSale:
          typeOfSaleOpportunitySelections.value.find((i) => i.value === opportunity.typeOfSale)
            ?.name || '',
        _salesFamily:
          salesFamilyOpportunitySelections.value.find((i) => i.value === opportunity.salesFamily)
            ?.name || '',
        _commercial: !opportunity.userName ? ' ' : opportunity.userName,
        _forecast: opportunity.priority < 3 ? 'Faible' : 'Fort',
        _dateDeadline: formatDate(opportunity.dateDeadline),
        _stage: opportunity.stageId ? stageMap.value.get(opportunity.stageId) : ' ',
        _expectedRevenue: printCurrency(opportunity.expectedRevenue),
        _recurringRevenue: printCurrency(opportunity.recurringRevenue),
        _totalRevenue: printCurrency(opportunity.expectedRevenue + opportunity.recurringRevenue),
      }
    }) || []
  )
})

function openModal(opportunity: Opportunity) {
  selectedOpportunity.value = opportunity
  showModal.value = true
}

function closeModal() {
  selectedOpportunity.value = null
  showModal.value = false
}

onMounted(async () => {
  await opportunityStore.getAllStages()
})
</script>

<template>
  <grid-vue
    @openModal="openModal"
    :list="opportunitiesList"
    :cols="[
      { field: '_partnerName', headerName: 'Société' },
      { field: '_commercial', headerName: 'Commercial' },
      { field: '_salesFamily', headerName: 'Portefeuille' },
      { field: 'name', headerName: 'Affaire' },
      { field: '_typeOfSale', headerName: 'Vente' },
      {
        field: '_dateDeadline',
        headerName: 'Date',
        filter: 'agDateColumnFilter',
        comparator: dateComparator,
      },
      {
        field: '_forecast',
        headerName: 'Forecast',
        sort: 'desc',
        cellRenderer: ArrowForecast,
        cellClass: 'cell-forecast',
      },
      {
        field: '_expectedRevenue',
        headerName: 'One Shot',
        comparator: currencyComparator,
      },
      {
        field: '_recurringRevenue',
        headerName: 'Récurrent',
        comparator: currencyComparator,
      },
      {
        field: '_totalRevenue',
        headerName: 'Total',
        comparator: currencyComparator,
      },
      { field: '_stage', headerName: 'Étape', cellClass: 'border-0' },
      {
        field: 'actions',
        headerName: 'Actions',
        resizable: false,
        filter: false,
        minWidth: 100,
        maxWidth: 100,
        cellRenderer: OpportunityGridActions,
        cellRendererParams: {
          onClick: (params: Opportunity) => {
            openModal(params)
          },
        },
        sortable: false,
        cellClass: 'border-0 grid-action',
      },
    ]"
  >
    <template #button>
      <OpportunityCreate v-if="showAddButton" />
    </template>
  </grid-vue>
  <OpportunityEdit
    v-if="showModal && selectedOpportunity"
    :opportunity="selectedOpportunity"
    @close="closeModal"
    :showModal="showModal"
  />
</template>

<style>
.cell-forecast > .ag-cell-wrapper {
  height: inherit;
}
</style>
